import { useMemo } from "react";
import cx from "classnames";

import CopyField from "components/CopyField";
import { StackOutput } from "types/generated";
import { isJson, isJsonBlob } from "utils/json";
import CodeEditor from "components/CodeEditor";

import styles from "./styles.module.css";

type OutputsItemProps = {
  item: StackOutput;
  onEditorMount?: () => void;
};

const OutputsItem = ({ item, onEditorMount }: OutputsItemProps) => {
  const isValueJsonBlob = useMemo(() => item.value && isJsonBlob(item.value), [item.value]);
  const value = useMemo(() => {
    if (!item.value) {
      return item.value;
    }

    try {
      if (isValueJsonBlob) {
        return JSON.stringify(JSON.parse(item.value), null, 2);
      }

      // Scalar output values (e.g. multi-line strings) are still JSON-encoded on the wire,
      // so they must be unwrapped rather than re-stringified, or escape sequences and
      // surrounding quotes leak into the rendered value.
      if (isJson(item.value)) {
        const parsed = JSON.parse(item.value);
        return typeof parsed === "string" ? parsed : String(parsed);
      }

      return item.value;
    } catch {
      // we do not show copy field for this value
      return undefined;
    }
  }, [item.value, isValueJsonBlob]);

  const nonSensitiveValue = value || "Value for this output is not available.";
  const valueChildren = item.sensitive ? "Value for this output is sensitive." : nonSensitiveValue;

  return (
    <li className={styles.listItem} id={item.id}>
      <div
        className={cx(styles.name, {
          [styles.nameSticky]: !item.sensitive,
          [styles.copyFieldPresent]: !item.sensitive && typeof value === "string",
        })}
      >
        <span>{item.id}</span>
        {!item.sensitive && typeof value === "string" && (
          <CopyField
            fullWidth={false}
            title="Copy Outputs"
            value={value}
            className={styles.copyOutputs}
          />
        )}
      </div>
      <div className={styles.value}>
        <CodeEditor
          body={valueChildren || ""}
          // eslint-disable-next-line
          language={`${item.sensitive ? "plaintext" : isValueJsonBlob ? "json" : "shell"}`}
          maxAutoHeight={Infinity}
          options={{
            lineNumbers: "off",
            glyphMargin: false,
          }}
          autoHeight
          readOnly
          onMountCallback={onEditorMount}
          skeletonCount={2}
        />
      </div>
    </li>
  );
};

export default OutputsItem;
